import { decodeJwtClaims, useEffectEvent } from '@funkit/connect-core'
import type { TFunction } from 'i18next'
import { useCallback, useState } from 'react'

import { flowError, identityAssertionErrorCode } from '../flow/flowError'
import type { GetIdentityAssertion } from '../identity/IdentityContext'
import { logger } from '../logging/logger'
import type { DepositOutcomeReporter } from './depositOutcome'

/** What the fiat flow needs from the host's minter, wrapped for one user. */
export interface FiatIdentityAssertion {
  getAssertion: GetIdentityAssertion
  /** The user the assertion is minted for; bounds the transport's cache. */
  subject: string
}

/**
 * The host's identity minter as the fiat flow sees it: wrapped so each
 * contract error code lands where the spec says, and read for the email the
 * assertion actually carries.
 *
 * - `SESSION_EXPIRED` closes the flow and reports `session_expired`; the host
 *   re-authenticates and reopens.
 * - `ACCOUNT_INELIGIBLE` reports `blocked` and is terminal for this account.
 * - `TRANSIENT`, or no code, reports `identity_assertion_failed`; the
 *   existing retry UI is the recovery.
 */
export function useIdentityAssertion({
  getIdentityAssertion,
  userId,
  outcome,
  onCloseFlow,
  t,
}: {
  getIdentityAssertion: GetIdentityAssertion | undefined
  userId: string
  outcome: DepositOutcomeReporter
  onCloseFlow: () => void
  t: TFunction
}): {
  /** `undefined` while the host declares no identity. */
  identityAssertion: FiatIdentityAssertion | undefined
  /** The `email` claim of an assertion minted for the current user. */
  assertedEmail: string | undefined
  /** The minter refused this account; nothing in the flow recovers it. */
  accountIneligible: boolean
} {
  // Keyed to the subject so a mid-flow user swap drops the previous user's.
  const [asserted, setAsserted] = useState<{ subject: string; email: string }>()
  const [accountIneligible, setAccountIneligible] = useState(false)

  const reportFailure = useEffectEvent((error: unknown) => {
    const code = identityAssertionErrorCode(error)
    // Name and message only: the host's error object may carry its session.
    logger.warn('connect-rn:fiat:identity_assertion_failed', {
      code,
      error: error instanceof Error ? error.name : typeof error,
      message: error instanceof Error ? error.message : undefined,
    })
    switch (code) {
      case 'SESSION_EXPIRED':
        outcome.failed(flowError('session_expired', t('fiat.sessionExpired')))
        onCloseFlow()
        return
      case 'ACCOUNT_INELIGIBLE':
        outcome.failed(
          flowError('blocked', t('fiat.accountIneligibleTitle'), {
            isRetryable: false,
            isFatal: true,
          }),
        )
        setAccountIneligible(true)
        return
      default:
        outcome.failed(
          flowError('identity_assertion_failed', t('fiat.submitFailed')),
        )
    }
  })

  const getAssertion = useCallback(async () => {
    if (getIdentityAssertion === undefined) {
      throw new Error('getIdentityAssertion is not configured')
    }
    try {
      const assertion = await getIdentityAssertion()
      const email = decodeJwtClaims(assertion)?.email
      if (typeof email === 'string') {
        setAsserted({ subject: userId, email })
      }
      return assertion
    } catch (error) {
      reportFailure(error)
      throw error
    }
  }, [getIdentityAssertion, reportFailure, userId])

  return {
    identityAssertion:
      getIdentityAssertion === undefined
        ? undefined
        : { getAssertion, subject: userId },
    assertedEmail:
      asserted !== undefined && asserted.subject === userId
        ? asserted.email
        : undefined,
    accountIneligible,
  }
}
